import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import Swal from "sweetalert2";

function UniversitiesTable(props) {
  const [rowData, setRowData] = useState([]);

  const fetchUniversitiesData = useCallback(() => {
    axios
      .get("https://backend.form-seef.com/api/universities")
      .then((res) => {
        if (res.data && res.data.data) {
          const universities = res.data.data.map((item, index) => ({
            id: item.id,
            serialNo: index + 1,
            universityName: item.name,
          }));

          setRowData(universities);
          props.setFormDataLength(universities.length);
        }
      })
      .catch((err) => {
        console.log(err);
      });
  }, [props]);

  useEffect(() => {
    fetchUniversitiesData();
  }, [fetchUniversitiesData]);

  const handleAddUniversity = async () => {
    const { value: userInput } = await Swal.fire({
      title: "Add University",
      input: "text",
      inputPlaceholder: "University name",
      showCancelButton: true,
      confirmButtonText: "Add",
      inputValidator: (value) => {
        if (!value) {
          return "You need to enter a university name!";
        }
      },
    });

    if (userInput) {
      axios
        .post("https://backend.form-seef.com/api/universities", { name: userInput })
        .then(() => {
          Swal.fire("University added successfully");
          fetchUniversitiesData();
        })
        .catch((error) => {
          console.log(error);
          Swal.fire("Error occurred while adding university");
        });
    }
  };

  const handleExport = () => {
    console.log("Exporting data...");
    // Perform export logic here
  };

  const handleEditUniversity = (id) => {
    const existingUniversity = rowData.find((university) => university.id === id);
    if (existingUniversity) {
      Swal.fire({
        title: "Edit University",
        input: "text",
        inputValue: existingUniversity.universityName,
        inputPlaceholder: "Enter the university name",
        showCancelButton: true,
        inputValidator: (value) => {
          if (!value) {
            return "University name cannot be empty";
          }
        },
      }).then((result) => {
        if (result.isConfirmed) {
          const updatedUniversity = { id: existingUniversity.id, name: result.value };
          axios
            .put(`https://backend.form-seef.com/api/universities/${existingUniversity.id}`, updatedUniversity)
            .then(() => {
              Swal.fire("University updated successfully");
              fetchUniversitiesData();
            })
            .catch((error) => {
              console.log(error);
              Swal.fire("Error occurred while updating university");
            });
        }
      });
    }
  };

  const handleDeleteUniversity = (id) => {
    const existingUniversity = rowData.find((university) => university.id === id);
    if (existingUniversity) {
      Swal.fire({
        title: "Delete University",
        text: `Are you sure you want to delete the university "${existingUniversity.universityName}"?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Delete",
      }).then((result) => {
        if (result.isConfirmed) {
          axios
            .delete(`https://backend.form-seef.com/api/universities/${existingUniversity.id}`)
            .then(() => {
              Swal.fire("University deleted successfully");
              fetchUniversitiesData();
            })
            .catch((error) => {
              console.log(error);
              Swal.fire("Error occurred while deleting university");
            });
        }
      });
    }
  };

  const handleDeleteAllUniversities = () => {
    Swal.fire({
      title: "Delete All Universities",
      text: "Are you sure you want to delete all selected universities?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Delete",
    }).then((result) => {
      if (result.isConfirmed) {
        const selectedUniversities = rowData.filter((university) => university.isSelected);
        const universityIds = selectedUniversities.map((university) => university.id);
        axios
          .delete("https://backend.form-seef.com/api/universities", { data: { ids: universityIds } })
          .then(() => {
            Swal.fire("Selected universities deleted successfully");
            fetchUniversitiesData();
          })
          .catch((error) => {
            console.log(error);
            Swal.fire("Error occurred while deleting selected universities");
          });
      }
    });
  };

  const columns = [
    { field: "serialNo", headerName: "Serial No", width: 100 },
    { field: "universityName", headerName: "Name of University", width: 450 },
    {
      field: "actions",
      headerName: "Actions",
      width: 200,
      sortable: false,
      renderCell: (params) => (
        <div className="flex space-x-2">
          <button
            className="edit-button bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded"
            onClick={() => handleEditUniversity(params.row.id)}
          >
            Edit
          </button>
          <button
            className="delete-button bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
            onClick={() => handleDeleteUniversity(params.row.id)}
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  const tableStyle = {
    height: 400,
    width: "100%",
    background: "#fff",
    border: "1px solid #ddd",
    borderRadius: "4px",
  };

  const cellStyle = {
    borderBottom: "1px solid #ddd",
    borderRight: "1px solid #ddd",
  };

  return (
    <div>
      <div className="" style={{ width: "100%" }}>
        <div className="flex space-x-4 mb-4 mt-4">
          <button
            className="bg-blue-500 hover:bg-blue-700 text-white font-semibold py-1 px-2 rounded"
            onClick={handleAddUniversity}
          >
            Add University
          </button>
          <button
            className="bg-red-500 hover:bg-red-700 text-white font-semibold py-1 px-2 rounded"
            onClick={handleDeleteAllUniversities}
          >
            Delete All Selected
          </button>
        </div>
      </div>
      <div className="bg-white h-96 p-3">
        <DataGrid
          rows={rowData}
          columns={columns}
          pageSize={5}
          checkboxSelection
          components={{
            Toolbar: GridToolbar,
          }}
          rowStyle={cellStyle}
          headerClassName="table-header"
        />
      </div>
    </div>
  );
}

export default UniversitiesTable;
